import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { css } from '@emotion/react';
import { Grid } from '@material-ui/core';
import { VerticalTabs, VerticalTab } from '@/components/Tabs';
import { EDIT_ORDER_TABS, EDIT_ORDER_TABS_DISPLAY, EDIT_ORDER_TABS_ICON } from '../../../constants';
import { ModalContext } from '../context';
import PickupTab from '../tabs/PickupTab';
import ItemsEditTab from '../tabs/ItemsEditTab';
import ServicesTab from '../tabs/ServicesTab';
import HaulAwayTab from '../tabs/HaulAwayTab';
import AdminTab from '../tabs/AdminTab';
import WarehouseTab from '../tabs/WarehouseTab';
import NotesTab from '../tabs/NotesTab';
import NotificationsTab from '../tabs/NotificationsTab';
import OrderDetailsTab from '../tabs/OrderDetailsTab';
import CustomerTab from '../tabs/CustomerTab';
import OriginTab from '../tabs/OriginTab';
import AdminNotes from '../tabs/AdminTab/AdminNotes';
import AdminRatesTab from '../tabs/AdminRatesTab';

/**
 * The tab rail and the scrolling body that holds every tab section. Reads `tabs`, `initTab`,
 * `initTabOffset`, and `hasErrorTab` from `ModalContext`, so render it inside the provider.
 */
const EditModalContent = () => {
    const [tab, setTab] = useState(0);
    const containerRef = useRef(null);

    const {
        state: { tabs, initTab, initTabOffset, hasErrorTab },
    } = useContext(ModalContext);

    const singleTab = useMemo(() => {
        return tabs.length === 1 && initTab === tabs[0];
    }, [tabs, initTab]);

    const scrollToTab = (tabId) => {
        const children = containerRef.current?.children || [];
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            if (child.id === tabId) {
                const containerRect = containerRef.current.getBoundingClientRect();
                const elementRect = child.getBoundingClientRect();
                const relativeTop = elementRect.top - containerRect.top;
                const targetScroll = containerRef.current.scrollTop + relativeTop - (initTabOffset || 0);
                containerRef.current.scrollTo({
                    top: targetScroll,
                    behavior: 'smooth',
                });
                break;
            }
        }
    };

    useEffect(() => {
        if (initTab) {
            scrollToTab(initTab);
        }
    }, [initTab]);

    const renderTab = (tab) => {
        let content;

        switch (tab) {
            case EDIT_ORDER_TABS.ORIGIN:
                content = <OriginTab />;
                break;
            case EDIT_ORDER_TABS.PICKUP:
                content = <PickupTab />;
                break;
            case EDIT_ORDER_TABS.CUSTOMER:
                content = <CustomerTab />;
                break;
            case EDIT_ORDER_TABS.ORDER_DETAILS:
                content = <OrderDetailsTab />;
                break;
            case EDIT_ORDER_TABS.ITEMS:
                content = <ItemsEditTab />;
                break;
            case EDIT_ORDER_TABS.SERVICES:
                content = <ServicesTab />;
                break;
            case EDIT_ORDER_TABS.HAUL_AWAY:
                content = <HaulAwayTab />;
                break;
            case EDIT_ORDER_TABS.ADMIN:
                content = <AdminTab />;
                break;
            case EDIT_ORDER_TABS.NOTES:
                content = <NotesTab />;
                break;
            case EDIT_ORDER_TABS.NOTIFICATIONS:
                content = <NotificationsTab />;
                break;
            case EDIT_ORDER_TABS.ADMIN_NOTES:
                content = <AdminNotes />;
                break;
            case EDIT_ORDER_TABS.WAREHOUSE:
                content = <WarehouseTab />;
                break;
            case EDIT_ORDER_TABS.ADMIN_RATES:
                content = <AdminRatesTab />;
                break;
        }
        return content;
    };

    return (
        <>
            {!singleTab ? (
                <VerticalTabs
                    value={tab}
                    onChange={(e, idx) => {
                        const tabId = tabs[idx];
                        scrollToTab(tabId);
                    }}
                >
                    {tabs.map((tab, idx) => {
                        const TabIcon = EDIT_ORDER_TABS_ICON[tab];
                        return (
                            <VerticalTab
                                key={tab}
                                label={EDIT_ORDER_TABS_DISPLAY[tab]}
                                icon={TabIcon ? <TabIcon /> : null}
                                value={idx}
                                error={hasErrorTab[tab]}
                            />
                        );
                    })}
                </VerticalTabs>
            ) : null}
            <Grid
                ref={containerRef}
                css={css`
                    flex-grow: 1;
                    padding: 16px 32px;
                    height: 100%;
                    overflow-y: auto;
                    display: flex;
                    flex-direction: column;
                    flex-wrap: nowrap;
                `}
                onScroll={(e) => {
                    let tabId;
                    const { clientHeight, scrollHeight, scrollTop, children } = e.target;
                    const scrollBottom = scrollTop + clientHeight;

                    if (scrollTop < 100) {
                        const child = children[0];
                        tabId = child.id;
                    } else if (scrollHeight - scrollBottom < 100) {
                        const child = children[children.length - 1];
                        tabId = child.id;
                    } else {
                        const center = scrollTop + clientHeight / 2;
                        let sum = 0;
                        for (let i = 0; i < children.length; i++) {
                            const child = children[i];
                            sum += child.clientHeight;
                            if (sum > center && !!child.id) {
                                tabId = child.id;
                                break;
                            }
                        }
                    }

                    const idx = tabs.indexOf(tabId);
                    if (idx >= 0 && idx !== tab) {
                        setTab(idx);
                    }
                }}
            >
                {tabs.map((tab, i) => (
                    <React.Fragment key={tab}>
                        {i > 0 ? (
                            <Grid
                                item
                                css={css`
                                    width: calc(100% + 64px);
                                    margin-left: -32px;
                                    margin-right: -32px;
                                    margin-top: 10px;
                                `}
                            >
                                <hr
                                    css={css`
                                        margin: 0;
                                        border: none;
                                        border-top: 1px solid black;
                                    `}
                                />
                            </Grid>
                        ) : null}
                        <Grid
                            id={tab}
                            container
                            direction="column"
                            css={css`
                                flex-wrap: nowrap;
                                width: 100%;
                                padding-top: 20px;
                            `}
                        >
                            {renderTab(tab)}
                        </Grid>
                    </React.Fragment>
                ))}
            </Grid>
        </>
    );
};

export default EditModalContent;
